<script setup lang="ts">
/* eslint-disable max-lines */
import { computed, onMounted, ref, watch, watchEffect } from 'vue';
import Checkbox from '../Shared/Input/Checkbox/Checkbox.vue';
import MeService from '@/core/shared/me/me.service';
import { getUserName } from '@/core/shared/user-profile/utils';
import {
  type InterviewSchedulingRequestDto,
  InterviewType,
} from '@/core/shared/interview-scheduling/types/interview-scheduling.type';
import type { Contact, SearchableContact } from './types/interview-form.type';
import AddCustomInterviewContactOption from './AddCustomInterviewContactOption.vue';
import { isValidEmail } from '@/utils/forms';
import CalendarConnectionStatus from '@/components/Interview/CalendarConnectionStatus.vue';
import CalendarService from '@/core/shared/calendar/calendar.service';
import { ErrorService } from '@/core/shared/errors/error.service';
import type { UserProfile } from '@factoryfixinc/ats-interfaces';
import CalendarConnectionUpgradeWarning from './CalendarConnectionUpgradeWarning.vue';
import VideoInterviewCalendarWarning from './VideoInterviewCalendarWarning.vue';
import InterviewSchedulingService from '@/core/shared/interview-scheduling/interview-scheduling.service';

const props = defineProps<{
  initialData?: Partial<InterviewSchedulingRequestDto>;
}>();

const emit = defineEmits<{
  (e: 'dataChange', data: InterviewSchedulingRequestDto): void;
}>();

const additionalDetails = ref('');
const schedulingPreferences = ref('');
const requireConfirmation = ref(true);
const venueDetails = ref('');
const contactSearch = ref('');
const customMeetingLink = ref('');

const selectedContacts = ref<Contact[]>([]);
const meService = new MeService();
const calendarService = new CalendarService();
const interviewSchedulingService = new InterviewSchedulingService();

const isVideoInterview = computed(() => interviewType.value === InterviewType.video);
const isOnSiteInterview = computed(() => interviewType.value === InterviewType.on_site);

const recentLocations = computed(() => interviewSchedulingService.recentLocations);

/**
 * Prefills the location with the most recent one when On-site is selected and the
 * field is still empty. The recruiter can freely overwrite it.
 */
function maybePrefillLocation() {
  if (isOnSiteInterview.value && !venueDetails.value && recentLocations.value.length > 0) {
    venueDetails.value = recentLocations.value[0];
  }
}

const formData = computed<InterviewSchedulingRequestDto>(() => ({
  contacts: selectedContacts.value.map((c) => ({
    name: c.name,
    email: c.email,
    userProfileId: c.id ?? null,
  })),
  duration: interviewDuration.value ?? null,
  type: interviewType.value ?? null,
  venueDetails: venueDetails.value,
  additionalDetails: additionalDetails.value,
  schedulingPreferences: schedulingPreferences.value,
  requireFinalConfirmation: requireConfirmation.value,
  customMeetingLink: customMeetingLink.value || undefined,
}));

async function populateFromInitialData(data: Partial<InterviewSchedulingRequestDto>) {
  await updateTeamMemberCalendarStatuses();

  if (data.contacts) {
    selectedContacts.value = data.contacts.map((c) => ({
      id: c.userProfileId,
      name: c.name,
      email: c.email,
      calendarConnected: teamMemberCalendarStatuses.value.get(c.userProfileId ?? 0) || false,
      exists: true,
    }));
  }
  if (data.duration) {
    interviewDuration.value = data.duration as Duration;
  }
  if (data.type) {
    interviewType.value = data.type;
  }
  if (data.venueDetails) {
    venueDetails.value = data.venueDetails;
  }
  if (data.additionalDetails) {
    additionalDetails.value = data.additionalDetails;
  }
  if (data.schedulingPreferences) {
    schedulingPreferences.value = data.schedulingPreferences;
  }
  if (data.requireFinalConfirmation !== undefined) {
    requireConfirmation.value = data.requireFinalConfirmation;
  }
  if (data.customMeetingLink) {
    customMeetingLink.value = data.customMeetingLink;
  }
}

const customContacts = ref<Contact[]>([]);
const teamMemberCalendarStatuses = ref<Map<number, boolean>>(new Map());

const contactList = computed<Contact[]>(() => {
  return [
    ...customContacts.value,
    ...(meService.employerUsers?.map((user) => ({
      id: user.id,
      name: resolveUserName(user),
      email: user.email,
      calendarConnected: teamMemberCalendarStatuses.value.get(user.id) || false,
      exists: true,
    })) ?? []),
  ].sort((a, b) => {
    return a.name.localeCompare(b.name);
  });
});

const customContactFromSearch = computed<Contact[]>(() => {
  if (!contactSearch.value) return [];
  return [
    {
      id: null,
      name: '',
      email: contactSearch.value,
      calendarConnected: false,
      exists: false,
    },
  ];
});

const availableContacts = computed<SearchableContact[]>(() => {
  return [...customContactFromSearch.value, ...contactList.value].map((c) => ({
    search: c.name || c.email,
    contact: c,
  }));
});

type Duration = 15 | 30 | 60;
const durations: { value: Duration; label: string }[] = [
  { value: 15, label: '15 mins' },
  { value: 30, label: '30 mins' },
  { value: 60, label: '60 mins' },
];
const interviewDuration = ref<Duration>();

const interviewType = ref<InterviewType>();
const interviewTypes: { value: InterviewType; label: string }[] = [
  { value: InterviewType.phone, label: 'Phone call' },
  { value: InterviewType.video, label: 'Video call' },
  { value: InterviewType.on_site, label: 'On-site interview' },
];

function selectCustomContactIfNew(email: string) {
  const contactNotInList = !contactList.value.find((c) => c.email === email);
  const emailIsValid = isValidEmail(email);
  if (contactNotInList && emailIsValid) {
    const newContact: Contact = {
      id: null,
      name: '',
      email,
      calendarConnected: false,
      exists: true,
    };
    customContacts.value.push(newContact);
    selectedContacts.value.push(newContact);
  }
}

function removeContact(email: string) {
  selectedContacts.value = selectedContacts.value.filter((p) => p.email !== email);
}

function resolveUserName(user: UserProfile): string {
  const currentUserName = getUserName(meService.userProfile);
  const userName = getUserName(user);
  return userName === currentUserName ? 'Your calendar' : userName;
}

watch(
  selectedContacts,
  (contacts) => {
    contactSearch.value = '';
    interviewSchedulingService.validateContacts(contacts);
  },
  {
    deep: true,
  },
);

watch(customMeetingLink, (newValue) => {
  interviewSchedulingService.validateCustomMeetingLink({
    needsUpgrade: calendarService.currentUserNeedsUpgrade,
    isVideoInterview: isVideoInterview.value,
    customMeetingLink: newValue,
  });
});

watch(interviewType, (newType, oldType) => {
  maybePrefillLocation();
  if (!oldType) {
    return; // avoid showing errors for initial type selection
  }
  interviewSchedulingService.validateCustomMeetingLink({
    needsUpgrade: calendarService.currentUserNeedsUpgrade,
    isVideoInterview: isVideoInterview.value,
    customMeetingLink: customMeetingLink.value,
  });
});

// Locations may arrive after On-site is already selected (async fetch); prefill then too.
watch(recentLocations, () => maybePrefillLocation());

async function updateTeamMemberCalendarStatuses() {
  const userProfileIds = meService.employerUsers?.map((user) => user.id) ?? [];
  if (userProfileIds.length === 0) return;

  try {
    const statuses = await calendarService.getConnectionStatus(userProfileIds);
    teamMemberCalendarStatuses.value.clear();
    statuses.forEach((status) => {
      teamMemberCalendarStatuses.value.set(status.userProfileId, status.status === 'active');
    });
  } catch (error) {
    ErrorService.captureException(error);
  }
}

onMounted(async () => {
  void interviewSchedulingService.fetchRecentLocations();
  if (props.initialData) {
    await populateFromInitialData(props.initialData);
  } else {
    await updateTeamMemberCalendarStatuses();
  }
});

watchEffect(() => {
  emit('dataChange', structuredClone(formData.value));
});
</script>

<template>
  <div class="interview-scheduling-form flex flex-col gap-6">
    <section>
      <div class="grid grid-cols-12 justify-between gap-2">
        <h2 class="section-title col-span-8 mb-1">Interview team</h2>
        <div
          :class="{
            'order-last col-span-full mb-4': calendarService.connectionFlowActive,
            'col-span-4 justify-self-end': !calendarService.connectionFlowActive,
          }"
        >
          <CalendarConnectionStatus>
            <template #trigger>
              <span
                class="flex cursor-pointer flex-row gap-2 text-sm font-medium text-highlight-500 hover:text-highlight-600 hover:underline"
              >
                <img src="@/assets/svg/conversations/calendar-add.svg" class="h-4 w-4" />
                Connect your calendar
              </span>
            </template>
          </CalendarConnectionStatus>
        </div>
        <p
          class="col-span-12 mb-2 text-sm text-shade-820"
          :class="{
            'order-[2]': calendarService.connectionFlowActive,
          }"
        >
          Who needs to join this interview? Your AI recruiter will schedule the interview at a time
          when everyone is free, and all team members will receive invites after scheduling with the
          candidate.
        </p>
      </div>
      <CalendarConnectionUpgradeWarning />
      <v-autocomplete
        v-model="selectedContacts"
        v-model:search="contactSearch"
        :items="availableContacts"
        placeholder="Search team members or add by email"
        multiple
        clearable
        variant="plain"
        density="compact"
        item-title="search"
        item-value="contact"
        class="text-field plain-autocomplete light-autocomplete"
        :ripple="false"
        :error="!!interviewSchedulingService.validationErrors.contacts"
      >
        <template #append-inner>
          <img src="@/assets/svg/chevron-down.svg" class="mt-1 h-4 w-4" />
        </template>
        <template v-slot:chip="{ props, item }: { props: any; item: { value: Contact } }">
          <v-chip v-bind="props" class="rounded-full border border-highlight-100 bg-highlight-50">
            {{ item.value.name || item.value.email }}
            <img
              src="@/assets/svg/x.svg"
              class="ml-1 h-4 w-4"
              @click="() => removeContact(item.value.email)"
            />
          </v-chip>
        </template>

        <template v-slot:no-data>
          <AddCustomInterviewContactOption
            :contact-search="contactSearch"
            @add-contact="selectCustomContactIfNew"
          />
        </template>
        <template v-slot:item="{ props, item }: { props: any; item: { raw: SearchableContact } }">
          <template v-if="item.raw.contact.exists">
            <v-list-item
              v-bind="props"
              title=""
              :value="item"
              class="h-8 bg-white hover:bg-white"
              :ripple="false"
            >
              <template v-if="item.raw.contact.exists">
                <span class="mr-2 text-xs font-normal text-shade-900">
                  {{ item.raw.contact.name || item.raw.contact.email }}
                </span>
                <span class="text-xs font-normal text-shade-800" v-if="item.raw.contact.name">{{
                  item.raw.contact.email
                }}</span>
              </template>
              <template #append>
                <span
                  class="flex flex-row rounded-full bg-primary-0 px-2 text-xs text-primary-600"
                  v-if="item.raw.contact.calendarConnected"
                >
                  <img src="@/assets/svg/check.svg" class="h-4 w-4" />
                  Calendar connected
                </span>
              </template>
            </v-list-item>
          </template>
          <template v-else>
            <AddCustomInterviewContactOption
              :contact-search="contactSearch"
              @add-contact="selectCustomContactIfNew"
            />
          </template>
        </template>
      </v-autocomplete>
      <div v-if="interviewSchedulingService.validationErrors.contacts" class="text-error text-xs">
        {{ interviewSchedulingService.validationErrors.contacts }}
      </div>
    </section>

    <section>
      <h2 class="section-title mb-2">Interview duration</h2>

      <div class="flex gap-2">
        <div
          v-for="duration in durations"
          :key="duration.value"
          @click.prevent="interviewDuration = duration.value"
          :class="{
            'bg-black': interviewDuration === duration.value,
            'bg-white': interviewDuration !== duration.value,
          }"
          class="flex h-8 cursor-pointer items-center rounded-full border border-tint-80 px-3 py-1.5 text-sm font-normal transition-colors"
        >
          {{ duration.label }}
        </div>
      </div>
    </section>

    <section>
      <h2 class="section-title mb-2">Interview type</h2>

      <div class="flex gap-2">
        <div
          v-for="type in interviewTypes"
          @click.prevent="interviewType = type.value"
          :key="type.value"
          :class="{
            'bg-black': interviewType === type.value,
            'bg-white': interviewType !== type.value,
          }"
          class="flex h-8 cursor-pointer items-center rounded-full border border-tint-80 px-3 py-1.5 text-sm font-normal transition-colors"
        >
          {{ type.label }}
        </div>
      </div>

      <VideoInterviewCalendarWarning :is-video-interview="isVideoInterview" class="mt-4" />

      <Transition name="slide-up" mode="out-in">
        <div v-if="isVideoInterview" class="mt-4">
          <label class="section-subtitle">Meeting link*</label>
          <v-text-field
            v-model="customMeetingLink"
            placeholder="Paste your meeting link (Zoom, Meet, Teams, etc.)"
            variant="plain"
            density="compact"
            class="text-field text-sm"
            :error="!!interviewSchedulingService.validationErrors.customMeetingLink"
          />
          <div
            v-if="interviewSchedulingService.validationErrors.customMeetingLink"
            class="text-error text-xs"
          >
            {{ interviewSchedulingService.validationErrors.customMeetingLink }}
          </div>
          <p class="helper-text mt-1" v-else>
            Required unless you reconnect your calendar for automatic Meet/Teams links.
          </p>
        </div>
      </Transition>
    </section>

    <section>
      <h2 class="section-title mb-2">Interview details</h2>

      <div class="flex flex-col gap-6">
        <Transition name="slide-up" mode="out-in">
          <div v-if="interviewType === InterviewType.on_site">
            <label class="section-subtitle">Interview location</label>
            <v-combobox
              v-model="venueDetails"
              :items="recentLocations"
              placeholder="Enter interview location"
              variant="plain"
              density="compact"
              hide-details
              hide-no-data
              class="text-field plain-autocomplete light-autocomplete location-combobox"
              :ripple="false"
            >
              <template v-if="recentLocations.length > 0" #append-inner>
                <img src="@/assets/svg/chevron-down.svg" class="mt-1 h-4 w-4" />
              </template>
              <template #item="{ props: itemProps, item }">
                <v-list-item v-bind="itemProps" :title="item.raw" class="text-sm" />
              </template>
            </v-combobox>
          </div>
        </Transition>

        <div>
          <label class="section-subtitle">
            Additional details <span class="text-sm font-normal text-shade-800">(optional)</span>
          </label>
          <v-textarea
            v-model="additionalDetails"
            placeholder="Enter additional details"
            variant="plain"
            density="compact"
            hide-details
            rows="3"
            auto-grow
            class="text-field mt-1"
          />
          <p class="helper-text mt-1">These details will be shared with the candidate.</p>
        </div>

        <div>
          <label class="section-subtitle">
            Scheduling preferences
            <span class="text-sm font-normal text-shade-800">(optional)</span>
          </label>
          <v-textarea
            v-model="schedulingPreferences"
            placeholder="Enter your schedule preferences (e.g. No Fridays.)"
            variant="plain"
            density="compact"
            class="text-field mt-1"
            hide-details
            rows="3"
            auto-grow
          />
          <p class="helper-text mt-1">
            These preferences may be shared with the candidate to help schedule the interview.
          </p>
        </div>
      </div>
    </section>

    <section>
      <h2 class="section-title mb-2">Confirmation options</h2>
      <label
        for="requireConfirmation"
        class="text-gray-700 flex flex-row gap-3 text-sm font-normal"
      >
        <Checkbox id="requireConfirmation" v-model="requireConfirmation" />
        Require your confirmation before finalizing
      </label>
      <p class="helper-text">
        When checked, your AI recruiter will email you with the proposed interview time for approval
        before confirming with the candidate and sending calendar invites.
      </p>
    </section>
  </div>
</template>

<style scoped>
.interview-scheduling-form {
  .helper-text {
    @apply text-xs font-normal text-shade-800;
  }
  .section-title {
    @apply text-base font-bold text-black;
  }
  .section-subtitle {
    @apply text-sm font-bold text-black;
  }
  :deep(.v-field) {
    @apply my-[8px] transition-colors duration-300;
    @apply h-10 rounded-md !border border-tint-80 text-sm outline-tint-80 placeholder:text-shade-800;
    @apply focus:outline-none focus:ring-0;
    box-shadow: none;

    &.v-field--focused {
      @apply !border-highlight-500;
      @apply outline-0 ring-0;
    }

    .v-field__input {
      @apply px-3 py-2 text-sm;
      input {
        @apply bg-transparent text-sm leading-6;
      }
    }
    .v-icon {
      @apply transition-all duration-300;
      @apply hover:opacity-100;
    }

    .v-field__field {
      @apply !min-h-10;
    }

    textarea.v-field__input,
    input.v-field__input {
      @apply rounded-md text-sm placeholder:text-shade-800 focus:outline-none focus:ring-0;
    }
  }

  :deep(.v-textarea .v-field) {
    @apply !h-auto !min-h-0;

    .v-field__field {
      @apply !min-h-0;
    }
  }

  :deep(.v-input__details) {
    @apply hidden;
  }

  .light-autocomplete :deep(.v-field) {
    @apply border border-tint-80 bg-tint-20;
    .v-field__input {
      input {
        @apply text-shade-900 placeholder:text-[13px] placeholder:text-shade-800  placeholder:opacity-95;
        @apply focus:outline-none focus:ring-0 disabled:placeholder:text-tint-200;
      }
    }

    &:hover {
      @apply border-tint-80;
    }
    .v-autocomplete__selection-text {
      @apply text-sm leading-6 text-shade-900;
    }
    .v-icon {
      @apply text-shade-900;
    }
  }
  .plain-autocomplete {
    :deep(.v-field) {
      @apply w-full bg-white p-0 shadow-none;
      .v-field__input {
        input {
          @apply bg-transparent p-0 text-shade-900 placeholder:text-shade-800;
          @apply disabled:placeholder:text-tint-200;
        }
      }

      &.v-field--focused {
        @apply outline-none ring-0;
      }
      .v-icon {
        @apply text-shade-900 transition-all duration-300;
        @apply hover:opacity-100;
      }
      .v-autocomplete__selection-text {
        @apply text-sm leading-6 text-shade-900;
      }
    }
  }
  /* Single-select combobox: keep the resting selection text and the active
     search input at the same left offset and height so the value doesn't
     shift when focused */
  .location-combobox :deep(.v-field__input) {
    @apply flex-nowrap;
    .v-combobox__selection {
      @apply my-0 ms-0 h-6 ps-0 text-sm leading-6 text-shade-900;
    }
    .v-combobox__selection-text {
      @apply text-sm font-normal leading-6 tracking-normal text-shade-900;
    }
    input {
      @apply ms-0 h-6 ps-0;
    }
  }
  .location-combobox :deep(.v-field--focused .v-field__input input) {
    @apply !ps-3;
  }
  /* Slide-up transition styles */
  .slide-up-enter-active,
  .slide-up-leave-active {
    transition: all 0.3s ease;
  }

  .slide-up-enter-from {
    opacity: 0;
    transform: translateY(10px);
  }

  .slide-up-enter-to {
    opacity: 1;
    transform: translateY(0);
  }

  .slide-up-leave-from {
    opacity: 1;
    transform: translateY(0);
  }

  .slide-up-leave-to {
    opacity: 0;
    transform: translateY(-10px);
  }
}
</style>
